'use client';

import { useMemo } from 'react';
import Grid from '@mui/material/Grid';
import { Skeleton } from '@mui/material';
import { BrokerCard } from '../BrokerCard';
import { Container } from './styles';
import { useGetBrokerOfficeList } from '@/queris';
import { adapterBrokerOfficeList, adapterBrokerSearchParams } from './model/adapter';
import { CustomPagination, NoData } from '@/components';
import { useBrokerFilters } from '../../hooks/useBrokerFilters';

export const BrokerGrid = () => {
  const { filters, setFilters } = useBrokerFilters();
  const { name, town, zip, locType, locCode, locName, locPostcode, page, perPage } = filters;

  const searchParamsObj = useMemo(
    () =>
      adapterBrokerSearchParams(
        { name, town, zip, locType, locCode, locName, locPostcode },
        page,
        perPage,
      ),
    [name, town, zip, locType, locCode, locName, locPostcode, page, perPage],
  );

  const { data, isLoading, isPending } = useGetBrokerOfficeList(searchParamsObj);
  const brokers = useMemo(() => (data ? adapterBrokerOfficeList(data) : []), [data]);

  const handlePageChange = (newPage: number) => {
    window.scrollTo({ top: 0 });
    void setFilters({ page: newPage });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    window.scrollTo({ top: 0 });
    void setFilters({ perPage: newItemsPerPage, page: 0 });
  };

  return (
    <Container>
      <Grid container spacing={3}>
        {isLoading || isPending ? (
          Array.from({ length: perPage }).map((_, index) => <Preloader key={`skeleton-${index}`} />)
        ) : brokers.length === 0 ? (
          <NoData />
        ) : (
          brokers.map((broker) => (
            <Grid size={{ xs: 12, md: 6 }} key={broker.id}>
              <BrokerCard broker={broker} />
            </Grid>
          ))
        )}
      </Grid>
      <CustomPagination
        page={page}
        numFound={data?.numFound}
        itemsPerPage={perPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </Container>
  );
};

const Preloader = () => {
  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Skeleton
        animation="wave"
        variant="rectangular"
        width="100%"
        height={193}
        sx={{
          borderRadius: '8px',
          '&::after': {
            animationDuration: '0.8s',
          },
        }}
      />
    </Grid>
  );
};
